import React, { Fragment, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { isEmpty } from "lodash";
import DatetimeRangePicker from "react-datetime-range-picker";
import NumberFormat from "react-number-format";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  Label,
  Row,
} from "reactstrap";
import WizardError from "../auth/register/WizardError";
import Flag from "react-flags";
import FalconCardHeader from "../common/FalconCardHeader";
import Flex from "../common/Flex";
import { createVisitor } from "../../helpers/api/visitors";
import { cleanVePhone, getVePhoneFormat } from "../../helpers/utils";
import { PreloadState } from "../UI/preloading/PreloadState";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import { Toast } from "../UI/toast/Toast";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamation,
  faSave,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import ButtonIcon from "../common/ButtonIcon";

import VisitorQRCodeModal from "./VisitorQRCodeModal";

const CreateVisitor = () => {
  const history = useHistory();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    control,
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: false,
  });

  const [vehicle, setVehicle] = useState(false);
  const [prefix, setPrefix] = useState("V");
  const [start_at, setStart_at] = useState(moment());
  const [end_at, setEnd_at] = useState(moment());
  const [submitting, setSubmitting] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [createdVisitor, setCreatedVisitor] = useState(null);

  register("firstname");
  register("lastname");

  useEffect(() => {
    if (start_at) {
      clearErrors("start_at");
    } else {
      setError("start_at", { message: "La hora de llegada es requerida." });
    }
    if (end_at) {
      clearErrors("end_at");
    } else {
      setError("end_at", { message: "La hora de salida es requerida." });
    }
    // eslint-disable-next-line
  }, [end_at, start_at]);

  const save = (values) => {
    setSubmitting(true);
    createVisitor({
      ...values,
      start_at: start_at.format("YYYY-MM-DD HH:mm:ss.sss"),
      end_at: end_at.format("YYYY-MM-DD HH:mm:ss.sss"),
    })
      .then((response) => {
        if (response.code === SUCCESS_RESPONSE.CREATED) {
          setCreatedVisitor(response.data);
          setShowQR(true);
          Toast.fire({
            type: "success",
            title: `Visita registrada exitosamente.`,
          });
        }
      })
      .catch((error) => handleErrorMessage(error))
      .finally(() => setSubmitting(false));
  };

  return (
    <Fragment>
      <Card tag={Form} onSubmit={handleSubmit(save)}>
        <FalconCardHeader
          title={
            start_at.isSame(end_at, "day")
              ? "Registro de visitante"
              : "Registro de persona autorizada"
          }
          className="bg-white"
        />
        <PreloadState
          loading={submitting}
          message="Registrando visita por favor espere..."
        >
          <CardBody className="bg-light">
            <Row>
              <Col>
                <Row form>
                  <Col tag={FormGroup}>
                    <Label>Nombre</Label>
                    <Input
                      className={`${errors?.firstname?.message ? "border-danger" : ""
                        } form-control input-spin-none`}
                      type="text"
                      name="firstname"
                      innerRef={register({
                        required: {
                          value: true,
                          message: "El nombre del visitante es requerido.",
                        },
                        pattern: {
                          value:
                            /^[a-zA-ZÀ-ÿ\u00f1\u00d1]+(\s*[a-zA-ZÀ-ÿ\u00f1\u00d1]*)*[a-zA-ZÀ-ÿ\u00f1\u00d1]+$/i,
                          message: "Nombre del visitante inválido.",
                        },
                      })}
                    />
                    {errors.firstname && (
                      <WizardError
                        error={errors.firstname.message}
                        className="fs--1 font-weight-normal d-block"
                      />
                    )}
                  </Col>
                  <Col tag={FormGroup}>
                    <Label>Apellido</Label>
                    <Input
                      className={`${errors?.lastname?.message ? "border-danger" : ""
                        } form-control input-spin-none`}
                      type="text"
                      name="lastname"
                      innerRef={register({
                        required: {
                          value: true,
                          message: "El apellido del visitante es requerido.",
                        },
                        pattern: {
                          value:
                            /^[a-zA-ZÀ-ÿ\u00f1\u00d1]+(\s*[a-zA-ZÀ-ÿ\u00f1\u00d1]*)*[a-zA-ZÀ-ÿ\u00f1\u00d1]+$/i,
                          message: "Apellido del visitante inválido.",
                        },
                      })}
                    />
                    {errors.lastname && (
                      <WizardError
                        error={errors.lastname.message}
                        className="fs--1 font-weight-normal d-block"
                      />
                    )}
                  </Col>
                </Row>
                <Row form>
                  <Col tag={FormGroup}>
                    <Label>DNI (Cédula de identidad o RIF)</Label>
                    <InputGroup>
                      <InputGroupAddon addonType="prepend">
                        <Input
                          type="select"
                          onChange={(e) => {
                            setPrefix(e.target.value);
                          }}
                        >
                          <option value="V">V</option>
                          <option value="E">E</option>
                          <option value="J">J</option>
                          <option value="P">P</option>
                          <option value="C">C</option>
                          <option value="G">G</option>
                        </Input>
                      </InputGroupAddon>
                      <Controller
                        name="dni"
                        control={control}
                        rules={{
                          required: {
                            value: true,
                            message: "El DNI es obligatorio",
                          },
                        }}
                        render={({ onChange, name, value }) => (
                          <NumberFormat
                            className={`${errors?.dni?.message ? "border-danger" : ""
                              } form-control input-spin-none`}
                            onValueChange={({ value }) => {
                              setValue("dni", `${prefix}${value}`);
                              if (!value) onChange(null);
                              else onChange(`${prefix}${value}`);
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={0}
                            renderText={(value, props) => (
                              <div {...props}>{value}</div>
                            )}
                          />
                        )}
                      />
                    </InputGroup>
                    {errors.dni && (
                      <WizardError
                        error={errors.dni.message}
                        className="fs--1 font-weight-normal d-block"
                      />
                    )}
                  </Col>
                </Row>
                <Row form>
                  <Col tag={FormGroup}>
                    <Label>Número de teléfono</Label>
                    <InputGroup>
                      <InputGroupAddon addonType="prepend">
                        <Flag
                          name="VE"
                          format="png"
                          pngSize={32}
                          className="mr-2"
                          shiny={false}
                          basePath="/flags"
                          alt="Venezuelan Flag"
                        />
                      </InputGroupAddon>
                      <Controller
                        name="phone"
                        control={control}
                        rules={{
                          required: {
                            value: true,
                            message: "El número te teléfono es requerido",
                          },
                        }}
                        render={({ onChange, name, value }) => (
                          <NumberFormat
                            className={`${errors?.phone?.message ? "border-danger" : ""
                              } form-control input-spin-none`}
                            value={value}
                            onValueChange={({ value }) => {
                              const cleaned = cleanVePhone(value);
                              setValue("phone", cleaned);
                              onChange(value);
                            }}
                            format={getVePhoneFormat(value)}
                            thousandSeparator={true}
                            renderText={(value, props) => (
                              <div {...props}>{value}</div>
                            )}
                          />
                        )}
                      />
                      {/*    <NumberFormat
                        placeholder="Télefono"
                        className="form-control input-spin-none"
                        {...register("phone", {
                          required: {
                            value: true,
                            message: "El número te teléfono es requerido",
                          },
                        })}
                        onValueChange={({ value }) => {
                          setValue("phone", value);
                        }}
                        format="+58 (###) ###-####"
                      /> */}
                    </InputGroup>
                    {errors.phone && (
                      <WizardError
                        error={errors.phone.message}
                        className="fs--1 font-weight-normal d-block"
                      />
                    )}
                    {/*   <WizardError error={errors["phone"]?.message} /> */}
                  </Col>
                </Row>
                <Row form>
                  <Col tag={FormGroup}>
                    <Label>Rango de estadía</Label>
                    <DatetimeRangePicker
                      startDate={start_at ? start_at : moment()}
                      endDate={end_at ? end_at : moment()}
                      pickerClassName="col-6 col-lg-6 col-md-12 col-sm-12 mb-sm-3 mb-md-3 mb-lg-0"
                      className="row"
                      timeFormat={"[a las] h:mm A"}
                      isValidStartDate={(current) => {
                        return current.isAfter(moment().add(-1, "days"));
                      }}
                      onChange={({ start, end }) => {
                        setStart_at(moment(start));
                        setEnd_at(moment(end));
                      }}
                      dateFormat={true}
                    />
                    <Row>
                      {errors.start_at && (
                        <>
                          {" "}
                          <WizardError
                            error={errors.start_at.message}
                            className="col-6 col-lg-6 col-md-12 col-sm-12"
                          />
                          <br />
                        </>
                      )}
                      {errors.end_at && (
                        <WizardError
                          className="col-6 col-lg-6 col-md-12 col-sm-12"
                          error={errors.end_at.message}
                        />
                      )}
                    </Row>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Alert tag={Flex} align="center" color="info">
                      <FontAwesomeIcon icon={faExclamation} className="mr-2" />
                      <p className="mb-0">
                        Si el rango de fecha es mayor a un día de diferencia el
                        visitante se registrará como <b>persona autorizada</b>.
                      </p>
                    </Alert>
                  </Col>
                </Row>
                <Row form>
                  <Col md={6} tag={FormGroup}>
                    <Label>Cantidad de entradas permitidas</Label>
                    <Input
                      type="number"
                      name="limit_entries"
                      defaultValue={1}
                      min={1}
                      className="form-control"
                      innerRef={register({
                        required: "Debe especificar la cantidad de entradas.",
                        min: { value: 1, message: "Mínimo 1 entrada." }
                      })}
                    />
                    {errors.limit_entries && (
                      <WizardError error={errors.limit_entries.message} />
                    )}
                  </Col>
                </Row>
                <Row form>
                  <Col tag={FormGroup}>
                    <Label>¿Vehículo?</Label>
                    <Input
                      type="checkbox"
                      value={vehicle}
                      onChange={({ target }) => {
                        setVehicle(target.checked);
                        setValue("plate_number", null);
                      }}
                      className="position-relative ml-2 mr-0"
                    />
                  </Col>
                </Row>
                {vehicle && (
                  <Row form>
                    <Col tag={FormGroup}>
                      <Label>Placa del vehículo</Label>
                      <Input
                        type="text"
                        name="plate_number"
                        style={{ textTransform: 'uppercase' }}
                        onChange={({ target }) => {
                          setValue("plate_number", target.value.toUpperCase());
                        }}
                        innerRef={register({
                          minLength: { value: 4, message: "Placa inválida." },
                          maxLength: { value: 10, message: "Placa inválida." },
                        })}
                      />
                      {errors.plate_number && (
                        <WizardError error={errors.plate_number.message} />
                      )}
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </CardBody>
          <CardFooter>
            <Flex align="center" justify="end">
              <ButtonIcon
                color="light"
                size="md"
                icon={faUndo}
                className="px-5 mr-2"
                onClick={() => history.push("/dashboard/visitors")}
              >
                Volver
              </ButtonIcon>
              <ButtonIcon
                color="primary"
                size="md"
                icon={faSave}
                className="px-5"
              /* disabled={!isEmpty(errors)} */
              >
                Registrar
              </ButtonIcon>
            </Flex>
          </CardFooter>
        </PreloadState>
      </Card>
      <VisitorQRCodeModal
        isOpen={showQR}
        toggle={() => {
          setShowQR(false);
          history.push("/dashboard/visitors");
        }}
        visitorData={createdVisitor}
      />
    </Fragment>
  );
};
export default CreateVisitor;
